/**
 * useAuth hook - Access auth state and methods
 *
 * Provides reactive auth state from Legend-State store
 */

'use client';

import { useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import { authStore$ } from '../modules/auth/stores/auth.store';
import type { CustomerToken, CustomerProfile } from '../modules/auth/models';

/**
 * Auth hook return type
 */
export interface UseAuthReturn {
    // State
    isAuthenticated: boolean;
    isLoading: boolean;
    customerToken: CustomerToken | undefined;
    customerProfile: CustomerProfile | undefined;
    error: string | undefined;

    // Actions
    setCustomerToken: (token: CustomerToken | undefined) => void;
    setCustomerProfile: (profile: CustomerProfile | undefined) => void;
    setIsAuthenticated: (value: boolean) => void;
    setIsLoading: (value: boolean) => void;
    setError: (error: string | undefined) => void;
    logout: () => void;
    clearError: () => void;
}

/**
 * Hook for auth state and actions
 *
 * @example
 * ```typescript
 * const { isAuthenticated, logout, customerProfile } = useAuth();
 *
 * if (isAuthenticated) {
 *   console.log('Welcome', customerProfile?.firstName);
 * }
 *
 * // Logout user
 * logout();
 * ```
 */
export const useAuth = (): UseAuthReturn => {
    // Reactive state selectors
    const isAuthenticated = useSelector(() => authStore$.isAuthenticated.get());
    const isLoading = useSelector(() => authStore$.isLoading.get());
    const customerToken = useSelector(() => authStore$.customerToken.get());
    const customerProfile = useSelector(() => authStore$.customerProfile.get());
    const error = useSelector(() => authStore$.error.get());

    // Actions
    const setCustomerToken = useCallback((token: CustomerToken | undefined) => {
        authStore$.customerToken.set(token);
    }, []);

    const setCustomerProfile = useCallback((profile: CustomerProfile | undefined) => {
        authStore$.customerProfile.set(profile);
    }, []);

    const setIsAuthenticated = useCallback((value: boolean) => {
        authStore$.isAuthenticated.set(value);
    }, []);

    const setIsLoading = useCallback((value: boolean) => {
        authStore$.isLoading.set(value);
    }, []);

    const setError = useCallback((error: string | undefined) => {
        authStore$.error.set(error);
    }, []);

    const logout = useCallback(() => {
        authStore$.customerToken.set(undefined);
        authStore$.customerProfile.set(undefined);
        authStore$.isAuthenticated.set(false);
        authStore$.isLoading.set(false);
        authStore$.error.set(undefined);
    }, []);

    const clearError = useCallback(() => {
        authStore$.error.set(undefined);
    }, []);

    return {
        // State
        isAuthenticated: isAuthenticated ?? false,
        isLoading: isLoading ?? false,
        customerToken,
        customerProfile,
        error,

        // Actions
        setCustomerToken,
        setCustomerProfile,
        setIsAuthenticated,
        setIsLoading,
        setError,
        logout,
        clearError,
    };
};

export default useAuth;
